<template>
    <div class="support">
        <template v-if="isDeviceLinked">
        <h1 class="support__title">{{ $t('SUPPORT_TICKETS') }}</h1>

        <div class="support__actions">
            <Button type="primary" @click="openCreateTicketModal">{{ $t('CREATE_SUPPORT_TICKET') }}</Button>
        </div>

        <div class="support__content">
            <div v-if="loading && tickets.length === 0" class="support__loading">
                <Loader />
            </div>
            <div v-else-if="tickets.length === 0 && !loading" class="support__empty">
                {{ $t('NO_SUPPORT_TICKETS') }}
            </div>
            <div v-else class="support__table-container">
                <table class="support__table">
                    <thead>
                        <tr>
                            <th>{{ $t('ID') }}</th>
                            <th>{{ $t('TOPIC') }}</th>
                            <th>{{ $t('STATUS') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="ticket in tickets" :key="ticket.id" @click="viewTicket(ticket)" class="support__row">
                            <td>{{ ticket.id }}</td>
                            <td class="support__topic">{{ ticket.topic }}</td>
                            <td>
                                <span class="support__status"
                                    :class="{ 'support__status--closed': ticket.status === 'closed' }">
                                    {{ ticket.status_translated }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Load More Button -->
                <div v-if="!isLastPage && !loading" class="support__load-more">
                    <Button type="secondary" @click="loadMoreTickets">{{ $t('LOAD_MORE') }}</Button>
                </div>

                <!-- Loading indicator for loading more -->
                <div v-if="loading && tickets.length > 0" class="support__loading-more">
                    <Loader />
                </div>
            </div>
        </div>

        <!-- Create Ticket Modal -->
        <VModal name="create-ticket-modal" :width="500" height="auto" :close-button="true" scrollable>
            <div class="create-ticket-modal">
                <h3 class="create-ticket-modal__title">{{ $t('CREATE_SUPPORT_TICKET') }}</h3>

                <div class="create-ticket-modal__form">
                    <div class="create-ticket-modal__field">
                        <label>{{ $t('TOPIC') }}</label>
                        <VInput v-model="newTicket.topic" :placeholder="$t('ENTER_TICKET_TOPIC')" />
                    </div>

                    <div class="create-ticket-modal__field">
                        <label>{{ $t('MESSAGE') }}</label>
                        <VTextarea v-model="newTicket.message" :placeholder="$t('DESCRIBE_YOUR_ISSUE')" :rows="5" />
                    </div>

                    <div class="create-ticket-modal__notice">
                        {{ $t('SUPPORT_ENGLISH_ONLY_NOTICE') }}
                    </div>

                    <!-- Troubleshooting Manual Validation -->
                    <div class="create-ticket-modal__validation">
                        <Checkbox v-model="hasReadManual" class="create-ticket-modal__checkbox">
                            <span>
                                {{ $t('I_HAVE_READ_TROUBLESHOOTING_MANUAL') }}
                                <a :href="troubleshootingManualUri" target="_blank" rel="noopener noreferrer"
                                    class="create-ticket-modal__link">{{ $t('TROUBLESHOOTING_MANUAL') }}</a>
                            </span>
                        </Checkbox>

                        <div class="create-ticket-modal__date-field">
                            <label>{{ $t('WHEN_WAS_TROUBLESHOOTING_MANUAL_UPDATED') }}</label>
                            <input type="date" v-model="manualLastUpdatedInput"
                                class="create-ticket-modal__date-input" />
                        </div>

                        <div v-if="!isManualValidationValid && (hasReadManual || manualLastUpdatedInput)"
                            class="create-ticket-modal__error">
                            {{ $t('TROUBLESHOOTING_MANUAL_VALIDATION_ERROR') }}
                        </div>
                    </div>
                </div>

                <div class="create-ticket-modal__buttons">
                    <Button @click="$modal.hide('create-ticket-modal')" type="secondary">
                        {{ $t('CANCEL') }}
                    </Button>
                    <Button @click="createTicket" type="primary" :disabled="!canSubmitTicket">
                        {{ $t('SUBMIT') }}
                    </Button>
                </div>
            </div>
        </VModal>
        </template>

        <div v-else class="support__requires-device">
            <h1 class="support__title">{{ $t('SUPPORT_TICKETS') }}</h1>
            <p class="support__requires-device-text">{{ $t('SUPPORT_REQUIRES_LINKED_DEVICE') }}</p>
            <Button tag="router-link" to="/link" type="primary">{{ $t('LINK_YOUR_DEVICE') }}</Button>
        </div>
    </div>
</template>

<script>
import Button from '@/components/Button.vue';
import VInput from '@/components/VInput.vue';
import VTextarea from '@/components/VTextarea.vue';
import VModal from '@/components/VModal.vue';
import Loader from '@/components/Loader.vue';
import Checkbox from '@/components/Checkbox.vue';
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'Support',
  components: {
    Button,
    VInput,
    VTextarea,
    VModal,
    Loader,
    Checkbox,
  },
  metaInfo() {
    return {
      title: this.$t('SUPPORT_TICKETS'),
      meta: [
        {
          name: 'description',
          content: this.$t('SUPPORT_TICKETS_META_DESCRIPTION'),
        },
      ],
    };
  },
  data() {
    return {
      tickets: [],
      loading: true,
      submitting: false,
      // Pagination state
      currentPage: 1,
      pageSize: 20,
      isLastPage: false,
      newTicket: {
        topic: '',
        message: '',
      },
      // Troubleshooting manual validation
      hasReadManual: false,
      manualLastUpdatedInput: '',
    };
  },
  computed: {
    ...mapState(['stats']),
    ...mapGetters(['isDeviceLinked']),
    troubleshootingManualUri() {
      return this.stats?.troubleshooting_manual_uri || '';
    },
    troubleshootingManualLastUpdated() {
      return this.stats?.troubleshooting_manual_last_updated || '';
    },
    isManualValidationValid() {
      if (!this.hasReadManual || !this.manualLastUpdatedInput) {
        return false;
      }
      // Compare dates in YYYY-MM-DD format
      return this.manualLastUpdatedInput === this.troubleshootingManualLastUpdated;
    },
    canSubmitTicket() {
      return this.newTicket.topic
                && this.newTicket.message
                && this.isManualValidationValid
                && !this.submitting;
    },
  },
  mounted() {
    if (this.isDeviceLinked) {
      this.fetchTickets();
    } else {
      this.loading = false;
    }
  },
  methods: {
    async fetchTickets(isLoadMore = false) {
      this.loading = true;

      try {
        const start = isLoadMore ? this.tickets.length : 0;

        const response = await this.$api.getSupportTickets({
          start,
          length: this.pageSize,
        });

        if (response.success) {
          const newTickets = response.data || [];

          if (isLoadMore) {
            // Append new tickets to existing ones
            this.tickets = [...this.tickets, ...newTickets];
          } else {
            // Replace tickets (initial load)
            this.tickets = newTickets;
            this.currentPage = 1;
            this.isLastPage = false;
          }

          // Check if this is the last page
          if (newTickets.length < this.pageSize) {
            this.isLastPage = true;
          }
        } else {
          this.$notify({
            type: 'error',
            title: this.$t('ERROR'),
            text: this.$t('FAILED_TO_LOAD_SUPPORT_TICKETS'),
          });
        }
      } catch (error) {
        console.error('Error fetching support tickets:', error);
        this.$notify({
          type: 'error',
          title: this.$t('ERROR'),
          text: this.$t('FAILED_TO_LOAD_SUPPORT_TICKETS'),
        });
      } finally {
        this.loading = false;
      }
    },

    async loadMoreTickets() {
      if (this.loading || this.isLastPage) {
        return;
      }

      this.currentPage++;
      await this.fetchTickets(true);
    },

    viewTicket(ticket) {
      this.$router.push(`/my/support/discussion=${ticket.id}`);
    },

    async openCreateTicketModal() {
      this.newTicket = {
        topic: '',
        message: '',
      };
      this.hasReadManual = false;
      this.manualLastUpdatedInput = '';

      // Fetch stats if not available
      if (!this.stats || !this.stats.troubleshooting_manual_uri) {
        await this.$store.dispatch('getStats');
      }

      this.$modal.show('create-ticket-modal');
    },

    async createTicket() {
      if (!this.newTicket.topic || !this.newTicket.message) {
        return;
      }

      this.submitting = true;

      try {
        // Make the API request
        const response = await this.$api.createSupportTicket({
          topic: this.newTicket.topic,
          message: this.newTicket.message,
        });

        console.log('Create ticket response:', response);

        // The response is the ticket data directly, not wrapped in a success object
        // Check if we have a valid ticket with an ID
        if (response && response.id) {
          // Success - show notification
          this.$notify({
            type: 'success',
            text: this.$t('SUPPORT_TICKET_CREATED_SUCCESSFULLY'),
          });

          // Close the modal
          this.$modal.hide('create-ticket-modal');

          const ticketId = response.id;
          console.log('New ticket ID:', ticketId);

          // Navigate to the new ticket
          setTimeout(() => {
            this.$router.push(`/my/support/discussion=${ticketId}`);
          }, 100);
        } else {
          // No valid ticket data
          this.$notify({
            type: 'error',
            text: this.$t('FAILED_TO_CREATE_SUPPORT_TICKET'),
          });

          // Refresh the tickets list
          await this.fetchTickets();
        }
      } catch (error) {
        console.error('Error creating support ticket:', error);
        this.$notify({
          type: 'error',
          text: this.$t('FAILED_TO_CREATE_SUPPORT_TICKET'),
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.support {
    padding: 15px;
    max-width: 1400px;
    //margin: 0 15px;

    &__title {
        font-size: 22px;
        font-weight: 600;
        margin-bottom: 20px;
        text-align: left;
    }

    &__actions {
        margin-bottom: 30px;
        display: flex;
        justify-content: flex-start;
    }

    &__content {
        min-height: 200px;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__loading {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 200px;
        width: 100%;
    }

    &__empty {
        color: var(--text-secondary);
        font-size: 16px;
        text-align: center;
    }

    &__requires-device {
        max-width: 520px;
        margin: 0 auto;
        text-align: center;
    }

    &__requires-device-text {
        color: var(--text-secondary);
        font-size: 16px;
        line-height: 1.5;
        margin-bottom: 20px;
    }

    &__table-container {
        width: 100%;
        overflow-x: auto;
        background-color: var(--bg-primary);
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        border: 1px solid var(--border-color);
    }

    &__table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;

        th,
        td {
            padding: 10px 12px;
            text-align: left;
            border-bottom: 1px solid var(--border-color);
            border-right: 1px solid var(--border-color-light, rgba(0, 0, 0, 0.05));
        }

        th:nth-child(1),
        td:nth-child(1) {
            width: 80px;
        }

        th:nth-child(3),
        td:nth-child(3) {
            width: 100px;
            text-align: center;
        }

        th:last-child,
        td:last-child {
            border-right: none;
        }

        th {
            font-weight: 600;
            color: var(--text-secondary);
            background-color: var(--bg-secondary);

            &:first-child {
                border-top-left-radius: 8px;
            }

            &:last-child {
                border-top-right-radius: 8px;
            }
        }

        tr:last-child td {
            border-bottom: none;
        }
    }

    &__row {
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: var(--bg-secondary, rgba(0, 0, 0, 0.03));
        }
    }

    &__topic {
        word-break: break-word;
        font-weight: 500;
    }

    &__status {
        display: inline-block;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        min-width: 70px;
        text-align: center;

        /* Status styling - using the same style as JIT badge */
        background-color: var(--lighter-primary);
        color: var(--primary);
        border: 1px solid rgba(0, 0, 0, 0.05);

        &--closed {
            background-color: var(--bg-success);
            color: black;
        }
    }

    &__load-more {
        display: flex;
        justify-content: center;
        padding: 20px;
        border-top: 1px solid var(--border-color);
        background-color: var(--bg-secondary, #f8f9fa);
    }

    &__loading-more {
        display: flex;
        justify-content: center;
        padding: 20px;
        border-top: 1px solid var(--border-color);
        background-color: var(--bg-secondary, #f8f9fa);
    }
}

.create-ticket-modal {
    padding: 15px;

    &__title {
        margin-bottom: 15px;
        font-size: 18px;
        font-weight: 600;
    }

    &__form {
        margin-bottom: 15px;
    }

    &__field {
        margin-bottom: 12px;

        label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
        }
    }

    &__notice {
        margin-top: 10px;
        padding: 8px 12px;
        background-color: #E9EEF9;
        border-radius: 6px;
        font-size: 13px;
        color: #333;
        border-left: 3px solid #2B7CFE;
    }

    &__validation {
        margin-top: 15px;
        padding: 12px;
        background-color: var(--bg-secondary, #f8f9fa);
        border-radius: 6px;
        border: 1px solid var(--border-color, #e0e0e0);
    }

    &__checkbox {
        margin-bottom: 12px;
        font-size: 14px;

        span {
            line-height: 1.4;
        }
    }

    &__link {
        color: var(--primary, #2B7CFE);
        text-decoration: none;
        font-weight: 500;

        &:hover {
            text-decoration: underline;
        }
    }

    &__date-field {
        margin-top: 12px;

        label {
            display: block;
            margin-bottom: 6px;
            font-weight: 500;
            font-size: 14px;
        }
    }

    &__date-input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid var(--border-color, #e0e0e0);
        border-radius: 4px;
        font-size: 14px;
        background-color: var(--bg-primary, #fff);
        color: var(--text-primary);
        transition: border-color 0.2s;

        &:focus {
            outline: none;
            border-color: var(--primary, #2B7CFE);
        }
    }

    &__error {
        margin-top: 10px;
        padding: 8px 12px;
        background-color: #FDECEA;
        border-radius: 4px;
        font-size: 13px;
        color: #D8000C;
        border-left: 3px solid #D8000C;
    }

    &__buttons {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
    }
}
</style>
